import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Trash2, Plus } from "lucide-react";
import { listShippingRates, createShippingRate, deleteShippingRate, formatApiErrorDetail } from "@/admin/adminApi";

const fmt = (cents) => `$${(cents / 100).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminShippingRatesListPage() {
  const [rates, setRates] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [newIsPOA, setNewIsPOA] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => listShippingRates().then(setRates);
  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError("");
    const priceCents = newIsPOA ? 0 : Math.round(parseFloat(newPrice) * 100);
    if (!newName.trim() || Number.isNaN(priceCents) || priceCents < 0) {
      setError("Enter a name and a valid price.");
      return;
    }
    setSaving(true);
    try {
      await createShippingRate({ name: newName.trim(), priceCents, isPOA: newIsPOA });
      setNewName("");
      setNewPrice("");
      setNewIsPOA(false);
      setShowAdd(false);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rate) => {
    if (!window.confirm(`Delete "${rate.name}"? Any products using it will fall back to the Default rate.`)) return;
    try {
      await deleteShippingRate(rate.id);
      await load();
    } catch (err) {
      window.alert(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  if (rates === null) return <p className="text-white/60 font-mono text-sm">Loading…</p>;

  return (
    <div data-testid="admin-shipping-rates-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-white">Shipping Rates</h1>
        <button data-testid="admin-shipping-rate-add-button" className="btn-primary" onClick={() => setShowAdd((v) => !v)}>
          <Plus size={16} /> Add Rate
        </button>
      </div>
      <p className="text-white/50 text-sm font-mono mb-4">
        Flat NZ-domestic rates. Click a rate to see and manage which products it applies to. Any product without an explicit rate uses "Default".
      </p>

      {showAdd && (
        <form onSubmit={handleAdd} data-testid="admin-shipping-rate-add-form" className="mb-6 p-4 border border-[var(--border-default)] bg-[var(--bg-secondary)]">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Rate Name</label>
              <input
                data-testid="admin-shipping-rate-name-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Bull Bars"
                className="w-full bg-[var(--bg-main)] border border-[var(--border-default)] px-3 py-2 text-sm text-white focus:border-[var(--brand-primary)] outline-none"
              />
            </div>
            <div className="w-40">
              <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Price (NZD)</label>
              <input
                data-testid="admin-shipping-rate-price-input"
                type="number"
                min="0"
                step="0.01"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                disabled={newIsPOA}
                placeholder={newIsPOA ? "POA" : "79.00"}
                className="w-full bg-[var(--bg-main)] border border-[var(--border-default)] px-3 py-2 text-sm text-white focus:border-[var(--brand-primary)] outline-none disabled:opacity-40"
              />
            </div>
            <button type="submit" disabled={saving} data-testid="admin-shipping-rate-save-button" className="btn-primary">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
          <label className="flex items-center gap-2 mt-3 cursor-pointer w-fit">
            <input
              type="checkbox"
              data-testid="admin-shipping-rate-poa-checkbox"
              checked={newIsPOA}
              onChange={(e) => setNewIsPOA(e.target.checked)}
              className="accent-[var(--brand-primary)]"
            />
            <span className="text-white/70 text-xs font-mono">
              Mark as POA (Price on Application) — for big/heavy items like Bush Cabin or Tray Canopies.
              Checkout still lets customers pay for the item now; Delivery shows as "to be invoiced separately" instead of a flat rate.
            </span>
          </label>
        </form>
      )}
      {error && <p className="text-red-400 text-sm font-mono mb-4" data-testid="admin-shipping-rate-error">{error}</p>}

      <div className="border-t border-[var(--border-default)]">
        {rates.map((rate) => (
          <div
            key={rate.id}
            data-testid={`admin-shipping-rate-row-${rate.id}`}
            className="flex items-center gap-4 border-b border-[var(--border-default)] py-4 bg-[var(--bg-secondary)] px-4"
          >
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">
                {rate.name}
                {rate.isDefault && (
                  <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-[var(--brand-primary)] border border-[var(--brand-primary)] px-1.5 py-0.5">Default</span>
                )}
                {rate.isPOA && (
                  <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-amber-400 border border-amber-400 px-1.5 py-0.5">POA</span>
                )}
              </p>
              <p className="text-white/40 text-xs font-mono mt-1">{rate.productCount} product{rate.productCount === 1 ? "" : "s"} assigned</p>
            </div>
            <div className="text-[var(--brand-primary)] font-mono text-sm w-24 shrink-0 text-right">{rate.isPOA ? "POA" : fmt(rate.priceCents)}</div>
            <Link to={`/admin/shipping-rates/${rate.id}`} data-testid={`admin-shipping-rate-edit-${rate.id}`} className="text-white/70 hover:text-[var(--brand-primary)] p-2">
              <Pencil size={16} />
            </Link>
            <button
              onClick={() => handleDelete(rate)}
              disabled={rate.isDefault}
              data-testid={`admin-shipping-rate-delete-${rate.id}`}
              className="text-white/70 hover:text-red-400 p-2 disabled:opacity-20 disabled:cursor-not-allowed"
              title={rate.isDefault ? "The Default rate cannot be deleted" : "Delete rate"}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
